import React, { useState, useEffect } from "react";
import { Button } from "./ui/Button";
import { Label } from "./ui/Label";
import { Textarea } from "./ui/Textarea";
import api from "../api";

/**
 * Simplified popup component for cognitive model reconstruction during session end.
 * Doctor can skip or enter details. No comparison shown.
 * Questions are therapy-specific (CBT, REBT, SFBT).
 */
export default function CognitiveModelReconstructionPopup({ 
  consultationId, 
  onSave, 
  onSkip,
  onCancel,
  isCustomPersona = false
}) {
  const [therapyType, setTherapyType] = useState("CBT");
  const [questions, setQuestions] = useState(null);
  const [categoryData, setCategoryData] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingQuestions, setLoadingQuestions] = useState(true);

  // Fetch therapy-specific questions on mount
  useEffect(() => {
    if (!isCustomPersona) {
      const fetchQuestions = async () => {
        try {
          setLoadingQuestions(true);
          // Pass consultation_id to get therapy type from consultation (not doctor's current setting)
          const response = await api.getCognitiveReconstructionQuestions(consultationId);
          setTherapyType(response.data.therapy_type || "CBT");
          setQuestions(response.data.questions || {});
          
          // Initialize category data with empty arrays
          const initialData = {};
          Object.keys(response.data.questions || {}).forEach(category => {
            initialData[category] = [""];
          });
          setCategoryData(initialData);
        } catch (err) {
          console.error("Error fetching therapy questions:", err);
          // Fallback to CBT questions
          const cbtQuestions = {
            core_beliefs: {
              title: "Core Beliefs",
              description: "Deep, fundamental beliefs (e.g., 'I am worthless')",
              placeholder: "e.g., I am worthless"
            },
            intermediate_beliefs: {
              title: "Intermediate Beliefs",
              description: "Rules or assumptions (e.g., 'If I make a mistake, people will reject me')",
              placeholder: "e.g., If I make a mistake, people will reject me"
            },
            automatic_thoughts: {
              title: "Automatic Thoughts",
              description: "Situation-specific thoughts (e.g., 'They're going to fire me')",
              placeholder: "e.g., They're going to fire me"
            },
            coping_strategies: {
              title: "Coping Strategies",
              description: "Behaviors or patterns (e.g., 'Avoid social situations')",
              placeholder: "e.g., Avoid social situations"
            }
          };
          setQuestions(cbtQuestions);
          setCategoryData({
            core_beliefs: [""],
            intermediate_beliefs: [""],
            automatic_thoughts: [""],
            coping_strategies: [""]
          });
        } finally {
          setLoadingQuestions(false);
        }
      };
      fetchQuestions();
    }
  }, [isCustomPersona]);

  const addItem = (category) => {
    setCategoryData(prev => ({
      ...prev,
      [category]: [...(prev[category] || [""]), ""]
    }));
  };

  const removeItem = (category, index) => {
    setCategoryData(prev => {
      const items = prev[category] || [""];
      if (items.length > 1) {
        return {
          ...prev,
          [category]: items.filter((_, i) => i !== index)
        };
      }
      return prev;
    });
  };

  const updateItem = (category, index, value) => {
    setCategoryData(prev => {
      const updated = [...(prev[category] || [""])];
      updated[index] = value;
      return {
        ...prev,
        [category]: updated
      };
    });
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Filter out empty items from all categories
      const reconstruction = {};
      Object.keys(categoryData).forEach(category => {
        reconstruction[category] = (categoryData[category] || []).filter(item => item.trim() !== "");
      });

      // Only save if at least one category has content
      const hasContent = Object.values(reconstruction).some(arr => arr.length > 0);
      
      if (hasContent) {
        await api.saveCognitiveReconstruction(consultationId, reconstruction);
      }
      
      if (onSave) {
        onSave(hasContent);
      }
    } catch (err) {
      console.error("Error saving reconstruction:", err);
      // Still proceed even if save fails
      if (onSave) {
        onSave(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    if (onSkip) {
      onSkip();
    }
  };

  const renderCategory = (categoryKey, categoryInfo) => {
    const items = categoryData[categoryKey] || [""];
    return (
      <div className="mb-4" key={categoryKey}>
        <Label className="text-base font-semibold mb-1 block">{categoryInfo.title}</Label>
        <p className="text-sm text-gray-600 mb-2">{categoryInfo.description}</p>
        <div className="space-y-2">
          {items.map((item, index) => (
            <div key={index} className="flex gap-2">
              <Textarea
                value={item}
                onChange={(e) => updateItem(categoryKey, index, e.target.value)}
                placeholder={categoryInfo.placeholder}
                className="flex-1 text-base"
                rows={2}
              />
              {items.length > 1 && (
                <Button
                  type="button"
                  onClick={() => removeItem(categoryKey, index)}
                  className="bg-red-500 hover:bg-red-600 px-2 py-1 text-base"
                >
                  ×
                </Button>
              )}
            </div>
          ))}
          <Button
            type="button"
            onClick={() => addItem(categoryKey)}
            className="bg-gray-400 hover:bg-gray-500 text-sm px-2 py-1"
          >
            + Add
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-purple-200 bg-purple-50">
          <h2 className="text-2xl font-bold text-purple-900">
            {isCustomPersona ? "End Session" : `Your Observation About Patient (${therapyType})`}
          </h2>
          {!isCustomPersona && (
            <p className="text-base text-purple-700 mt-1">
              Based on your session, enter the patient's cognitive model details (optional). You can skip this step.
            </p>
          )}
        </div>

        <div className="p-6">
          {!isCustomPersona ? (
            <>
              {loadingQuestions ? (
                <div className="text-center py-8">
                  <p className="text-gray-600">Loading questions...</p>
                </div>
              ) : questions ? (
                <>
                  {Object.keys(questions).map(categoryKey => 
                    renderCategory(categoryKey, questions[categoryKey])
                  )}
                </>
              ) : (
                <div className="text-center py-8">
                  <p className="text-red-600">Failed to load questions. Please try again.</p>
                </div>
              )}

              <div className="flex gap-4 mt-6 pt-4 border-t">
                <Button 
                  onClick={handleSave} 
                  disabled={loading} 
                  className="bg-purple-600 hover:bg-purple-700 flex-1 text-base"
                >
                  {loading ? "Saving..." : "Save & Close Session"}
                </Button>
                <Button 
                  onClick={handleSkip} 
                  disabled={loading}
                  className="bg-gray-600 hover:bg-gray-700 flex-1 text-base"
                >
                  Close Session
                </Button>
                <Button 
                  onClick={onCancel || handleSkip} 
                  disabled={loading}
                  className="bg-gray-400 hover:bg-gray-500 flex-1 text-base"
                >
                  Cancel
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="text-base text-gray-700">
                Are you sure you want to end the session?
              </p>
              <div className="flex gap-4 mt-6 pt-4 border-t">
                <Button 
                  onClick={handleSkip} 
                  disabled={loading}
                  className="bg-gray-600 hover:bg-gray-700 flex-1 text-base"
                >
                  Close Session
                </Button>
                <Button 
                  onClick={onCancel || handleSkip} 
                  disabled={loading}
                  className="bg-gray-400 hover:bg-gray-500 flex-1 text-base"
                >
                  Cancel
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

